/**
 * Stuck-webhook queue — the operator surface the replay sweep was missing
 * (roadmap item I), rendered inside the Tier-1 PlatformBudgetAdmin view.
 *
 * ── Why this screen exists ──
 *
 * The Stripe webhook endpoint answers 200 even when the handler throws — on
 * purpose, because an immediate Stripe retry would re-hit the same failure. So
 * Stripe never retries, our own sweep does, and after
 * `WEBHOOK_REPLAY_MAX_ATTEMPTS` the sweep gives up and logs a WARN once per
 * run. That log was the ONLY surface, which is the wrong home for it twice
 * over: a log line is invisible to whoever is not already tailing it, and the
 * event nobody has looked at is exactly the one it fails to surface.
 *
 * What is actually behind a row here: a customer was charged and their credit
 * was never granted. `reconcile-credits` is structurally blind to it — it
 * audits balance against ledger, and here BOTH are absent, so they agree with
 * each other while disagreeing with reality.
 *
 * ── Two deliberate shapes ──
 *
 * - **Empty is the good state, and says so.** A queue that renders nothing when
 *   empty looks identical to a queue that failed to load. This renders an
 *   explicit "nothing stuck" line so silence is never ambiguous.
 * - **Retry requeues, it does not replay.** The button zeroes `attempts`; the
 *   sweep picks the event up on its next pass. Replaying inline would put a
 *   multi-second Stripe write behind an admin click with no retry semantics of
 *   its own, and the sweep already owns ordering, batching and the bound. The
 *   copy says "queued for retry" rather than "retried" for that reason — the
 *   operator should not read a requeue as a fix.
 *
 * Copy lives in the `billing` i18n namespace (`webhooks.*`, all four locales),
 * matching PromotionsPanel — this renders in the same fully-localized shell.
 */
import { useCallback, useEffect, useState } from 'react'
import { RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { useToast } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { PLATFORM_DATE_LOCALE } from '@papan/types'
import { webhookAdminApi, type StuckWebhookEventView } from '../client/webhook-admin.js'
import { NUMERIC } from './styles.js'

// Event instants (received/processed) — Malaysia date order for this shelf
// panel, but kept in LOCAL time (an instant, not a civil billing date).
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString(PLATFORM_DATE_LOCALE) : '—')

/**
 * Split the last error into a human SUMMARY (the first line) and the stack
 * trace DETAIL (the `at …` frames). The first line already reads plainly
 * ("cannot resolve tenant for Stripe invoice … (no … mapping)"); the frames are
 * debugging detail — useful, but noise to scan — so the panel shows the summary
 * and collapses the detail behind a disclosure (#491 follow-up). Exported for
 * unit testing.
 */
export function splitError(error: string): { summary: string; detail: string | null } {
  const lines = error.split('\n')
  // Strip the leading "<ErrorName>: " — a stack's first line is
  // "Error: …" for a plain Error and "<SubclassName>Error: …" for a typed one
  // (markFailed records `err.stack`), so match any identifier ending in "Error".
  const summary = (lines[0] ?? error).replace(/^[\w$]*Error:\s*/, '')
  const detail = lines.slice(1).join('\n').trim()
  return { summary, detail: detail.length > 0 ? detail : null }
}

export function StuckWebhooksPanel({ canEdit = true }: {
  /** Phase 0 (D2): `operations:write`. Listing stuck events is a read; retrying
   *  one re-runs a payment-side effect, so the button needs the write. */
  canEdit?: boolean
} = {}) {
  const { t } = useTranslation('billing')
  const toast = useToast()
  const [events, setEvents] = useState<StuckWebhookEventView[]>([])
  const [truncated, setTruncated] = useState(false)
  const [maxAttempts, setMaxAttempts] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  /** null = the surface is unavailable (billing off ⇒ the route 404s), which is
   *  a different statement from "no stuck events" and must not render as one. */
  const [unavailable, setUnavailable] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await webhookAdminApi.listStuck()
      setEvents(r.events)
      setTruncated(r.truncated)
      setMaxAttempts(r.maxAttempts)
      setUnavailable(false)
    } catch {
      // Billing off, or the read failed. Either way we do not know the queue is
      // empty, so we must not imply it.
      setUnavailable(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const retry = useCallback(async (id: string) => {
    setBusyId(id)
    try {
      await webhookAdminApi.retry(id)
      toast.success(t('webhooks.requeued'))
      // Re-read rather than splicing the row out: the event is still `failed`
      // until a replay succeeds, so it legitimately stays in the list with
      // attempts reset. Removing it optimistically would claim a fix.
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('webhooks.requeueFailed'))
    } finally {
      setBusyId(null)
    }
  }, [load, t, toast])

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between gap-4 mb-1">
        <h2 className="themed-text text-lg font-medium flex items-center gap-2">
          {t('webhooks.title')}
          {events.length > 0 && (
            <span data-toc-ignore className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-medium tabular-nums">
              {events.length}{truncated ? '+' : ''}
            </span>
          )}
        </h2>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="text-xs themed-text-muted hover:themed-text flex items-center gap-1 transition-colors disabled:opacity-60 disabled:cursor-default"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : undefined} />
          {t('webhooks.refresh')}
        </button>
      </div>
      {/* Only when there is something to explain — same reason as the dunning
          queue beside it. On a manual-billing deployment no provider event can
          be stuck, so this paragraph was permanent furniture above the queues
          that do have rows. The empty state below still states that the queue
          was READ and was clear. */}
      {(unavailable || events.length > 0) && (
        <p className="themed-text-muted text-sm mb-4">{t('webhooks.subtitle')}</p>
      )}

      {unavailable && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2">
          <p className="text-amber-300 text-xs font-medium">{t('webhooks.unavailable')}</p>
          <p className="themed-text-secondary text-xs mt-0.5">{t('webhooks.unavailableBody')}</p>
        </div>
      )}

      {/* Empty is the GOOD state and is stated explicitly — an empty region
          would be indistinguishable from a failed load. */}
      {!unavailable && !loading && events.length === 0 && (
        <div className="flex items-center gap-2 themed-text-secondary text-sm">
          <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
          <span>{t('webhooks.empty')}</span>
        </div>
      )}

      {!unavailable && events.length > 0 && (
        <div className="space-y-2" data-testid="stuck-webhooks-list">
          <div className="flex items-center gap-2 text-xs text-amber-300">
            <AlertTriangle size={13} className="shrink-0" />
            <span>{t('webhooks.needsAttention', { count: events.length })}</span>
          </div>

          {events.map((e) => (
            <div key={e.id} className="themed-bg-secondary rounded-lg p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="themed-text text-sm font-medium truncate">{e.eventType}</p>
                  {/* The Stripe event id is the handle for the full object in
                      the Stripe dashboard — the payload is not sent here. */}
                  <p className={`themed-text-muted text-xs mt-0.5 truncate ${NUMERIC}`}>{e.eventId}</p>
                </div>
                <button
                  onClick={() => void retry(e.id)}
                  disabled={busyId === e.id || !canEdit}
                  className="themed-btn-secondary text-xs rounded px-2 py-1 shrink-0 disabled:opacity-60 disabled:cursor-default"
                >
                  {busyId === e.id ? t('webhooks.requeueing') : t('webhooks.retry')}
                </button>
              </div>

              <div className={`flex flex-wrap gap-x-4 gap-y-0.5 mt-2 text-xs themed-text-muted ${NUMERIC}`}>
                <span>
                  {maxAttempts != null
                    ? t('webhooks.attemptsOf', { attempts: e.attempts, max: maxAttempts })
                    : t('webhooks.attempts', { attempts: e.attempts })}
                </span>
                <span>{t('webhooks.received', { at: fmtDate(e.receivedAt) })}</span>
                <span>{t('webhooks.lastTried', { at: fmtDate(e.processedAt) })}</span>
              </div>

              {e.error && (() => {
                // The last error, not a history: `markFailed` overwrites it each
                // attempt. Summary (first line) shown inline so the row is
                // scannable; the stack trace — the whole reason an operator
                // opened this screen — collapses behind a native <details>
                // disclosure so it is one click away without being noise.
                const { summary, detail } = splitError(e.error)
                return (
                  <div className="mt-2">
                    <p className="themed-text-secondary text-xs break-words">{summary}</p>
                    {detail && (
                      <details className="mt-1">
                        <summary className="themed-text-muted text-[11px] cursor-pointer hover:themed-text">
                          {t('webhooks.errorDetails')}
                        </summary>
                        <pre className="themed-text-muted text-[11px] font-mono mt-1 overflow-x-auto whitespace-pre">
                          {detail}
                        </pre>
                      </details>
                    )}
                  </div>
                )
              })()}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
